import type { DeepPartialObject } from "@costar-search-x/core";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useRef } from "react";
import {
  handleSearchParamsChange,
  handleSearchXCriteriaChange,
} from "../criteria-url-helpers";
import type { SearchXStateType } from "../criteria-url-helpers";

type Props<C, U> = {
  baseCriteria: DeepPartialObject<C>;
  defaultCriteria: DeepPartialObject<C>;
  searchXState: SearchXStateType<C> | undefined;
  getCriteriaParams: (criteria: DeepPartialObject<C> | undefined) => U;
  pathName: string;
  convertUrlToCriteria: (
    key: keyof U,
    value: string,
    criteria: DeepPartialObject<C>
  ) => DeepPartialObject<C>;
};

export const useURLCriteriaSync = <
  C,
  U extends Record<string, string | number>
>({
  baseCriteria,
  defaultCriteria,
  searchXState,
  pathName,
  getCriteriaParams,
  convertUrlToCriteria,
}: Props<C, U>) => {
  const searchParams = useSearchParams();
  const router = useRouter();

  // useSearchParams() return ReadonlyURLSearchParams
  // need to sort to create unique string for comparison,
  const searchParams_ = new URLSearchParams(searchParams?.toString());
  searchParams_.sort();
  const searchParamsAsString = searchParams_?.toString() ?? "";
  const searchParamsAsStringRef = useRef(searchParamsAsString);

  const searchXInitialState = useRef<
    | {
        criteria: DeepPartialObject<C>;
        page: number;
      }
    | undefined
  >(undefined);

  if (!searchXInitialState.current) {
    //Read initial SearchX State from URL (or default criteria)
    //If searchParams start with NULL Criteria (other default criteria)
    // let updatedCriteria: DeepPartialObject<C> = searchParamsAsString
    //   ? structuredClone(baseCriteria)
    //   : structuredClone(defaultCriteria);
    let updatedCriteria: DeepPartialObject<C> = searchParamsAsString
      ? JSON.parse(JSON.stringify(baseCriteria))
      : JSON.parse(JSON.stringify(defaultCriteria));

    searchParams?.forEach((v, k) => {
      updatedCriteria = convertUrlToCriteria(k as keyof U, v, updatedCriteria);
    });

    searchXInitialState.current = {
      criteria: updatedCriteria,
      page: Number(searchParams?.get("page")) || 1,
    };
  }

  //handle changes to eith URL search Params or SearchX Criteria
  useEffect(() => {
    //URL Search Params change
    if (searchParamsAsString !== searchParamsAsStringRef.current) {
      searchParamsAsStringRef.current = searchParamsAsString;
      handleSearchParamsChange<C, U>({
        baseCriteria,
        searchParams,
        convertUrlToCriteria,
        searchXState,
      });
    } else {
      //change to SearchX criteira
      const criteria = searchXState?.searchState?.search?.criteria?.criteria;
      const page = searchXState?.searchState?.search?.pageNumber;
      if (
        criteria &&
        page &&
        searchParamsAsStringRef.current === searchParamsAsString
      ) {
        handleSearchXCriteriaChange<DeepPartialObject<C>, U>({
          criteria,
          searchParams,
          searchParamsAsStringRef,
          getCriteriaParams,
          page,
        });
      }
    }
  }, [
    baseCriteria,
    convertUrlToCriteria,
    getCriteriaParams,
    pathName,
    router,
    searchParams,
    searchParamsAsString,
    searchXState,
  ]);

  return searchXInitialState;
};
